<!-- CareManagement_MyTasks.vue -->
<!--
  Cross-patient CARE GRAPH to-do panel. One row per open care-graph `task` node
  across all patients. A task is "open" when its parent signal (via the `causes`
  edge) is not resolved. Pure care graph — NOT the operational state machine.
  Data: GET /api/careplannodes/todo/. Clicking a task opens the Patient_Drawer
  to the Care Graph tab.
-->
<template>
  <div class="flex flex-col" style="height: calc(100vh - 165px);">
    <!-- Header: title + summary chips -->
    <div class="px-4 py-2.5 border-b border-gray-200 bg-white shrink-0">
      <div class="flex items-center justify-between">
        <div>
          <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Care Graph To-Do</div>
          <!-- Stats skeleton -->
          <div v-if="loading" class="flex items-center gap-2">
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
          <!-- Stats loaded -->
          <div v-else class="flex items-center gap-2">
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <span class="text-base font-bold text-gray-900">{{ counts.total }}</span>
              <span class="text-[10px] uppercase tracking-wider text-gray-400">Open</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <span class="text-base font-bold text-gray-700">{{ counts.planned }}</span>
              <span class="text-[10px] uppercase tracking-wider text-gray-400">Planned</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-200">
              <span class="text-base font-bold text-blue-700">{{ counts.attempted }}</span>
              <span class="text-[10px] uppercase tracking-wider text-blue-600">Attempted</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <span class="text-base font-bold text-gray-700">{{ counts.patients }}</span>
              <span class="text-[10px] uppercase tracking-wider text-gray-400">Patients</span>
            </div>
          </div>
        </div>
        <button class="cm-icon-btn" title="Refresh" @click="loadTasks">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 12px;" />
        </button>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="px-4 py-2 flex items-center flex-wrap gap-3 border-b border-gray-200 bg-gray-50 shrink-0">
      <!-- Search -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Search</label>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Patient, task, or signal..."
          class="w-56 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <!-- Ownership scope: My Patients (my caseload) vs My To-Dos (to-dos
           assigned to me). De-selectable — clicking the active chip turns it
           off, showing the whole tenant's to-dos (for coverage/management).
           Selecting a specific Care Manager (below) also clears the scope so
           you see that person's list unfiltered. -->
      <button
        :class="chipClass(scopeFilter === 'my_patients', 'gray')"
        @click="setScope('my_patients')"
      >My Patients</button>
      <button
        :class="chipClass(scopeFilter === 'my_todos', 'blue')"
        @click="setScope('my_todos')"
      >My To-Dos</button>

      <!-- Care Manager filter: view another person's (or everyone's) to-dos.
           Filters rows by owner_entity_id. Reuses the already-loaded members. -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Care Manager</label>
        <select
          v-model="ownerFilter"
          class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 max-w-[180px]"
          @change="onOwnerFilterChange"
        >
          <option :value="null">All care managers</option>
          <option value="unassigned">Unassigned</option>
          <option v-for="m in sortedMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
        </select>
      </div>

      <!-- Status quick filters -->
      <button
        :class="chipClass(statusFilter === 'planned', 'gray')"
        @click="statusFilter = statusFilter === 'planned' ? null : 'planned'"
      >Planned</button>
      <button
        :class="chipClass(statusFilter === 'attempted', 'blue')"
        @click="statusFilter = statusFilter === 'attempted' ? null : 'attempted'"
      >Attempted</button>

      <div class="ml-auto flex items-center gap-2">
        <ExportMenu :disabled="!rows.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
    </div>

    <!-- Main content: task list (left) + drawer (right) -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Task list -->
      <div
        :style="{ width: selectedPatient ? splitPct + '%' : '100%' }"
        class="flex flex-col border-r border-gray-200 overflow-y-auto overflow-x-hidden min-w-0"
      >
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50 z-10 border-b border-gray-200">
            <tr class="text-left text-[11px] uppercase tracking-wider text-gray-500">
              <th class="px-4 py-2 font-semibold w-24">Status</th>
              <th class="px-3 py-2 font-semibold cursor-pointer w-32" @click="setSort('patient')">
                Patient <SortCaret :active="sortKey === 'patient'" :dir="sortDir" />
              </th>
              <th class="px-3 py-2 font-semibold w-[24%]">Signal</th>
              <th class="px-3 py-2 font-semibold">Task</th>
              <th class="px-3 py-2 font-semibold w-40">Assigned</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading skeleton -->
            <template v-if="loading">
              <tr v-for="n in 12" :key="'sk' + n" class="border-b border-gray-100">
                <td v-for="c in 5" :key="c" class="px-4 py-2.5">
                  <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: (40 + (c * 9) % 50) + '%' }"></div>
                </td>
              </tr>
            </template>

            <!-- Empty state -->
            <tr v-else-if="!filteredTasks.length">
              <td colspan="5" class="px-4 py-16 text-center text-gray-400">
                <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-2xl mb-2 text-green-300" />
                <div class="text-sm">{{ emptyStateText }}</div>
              </td>
            </tr>

            <!-- Task rows -->
            <template v-else>
              <tr
                v-for="row in filteredTasks"
                :key="row.task_id"
                :class="[
                  'border-b border-gray-100 cursor-pointer transition-colors',
                  selectedTaskId === row.task_id ? 'bg-primary-50' : 'hover:bg-gray-50'
                ]"
                @click="openTask(row)"
              >
                <!-- Status -->
                <td class="px-4 py-2.5 whitespace-nowrap align-top">
                  <span
                    class="inline-flex items-center gap-1.5 text-xs"
                    :class="row.task_status === 'attempted' ? 'text-blue-700' : 'text-gray-500'"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="row.task_status === 'attempted' ? 'bg-blue-500' : 'border border-gray-400'"
                    ></span>
                    {{ row.task_status === 'attempted' ? 'Attempted' : 'Planned' }}
                  </span>
                </td>
                <!-- Patient -->
                <td class="px-3 py-2.5 w-32 max-w-[128px] align-top">
                  <div class="text-xs text-gray-900 truncate" :title="row.patient_name">{{ titleCase(row.patient_name) }}</div>
                  <div class="text-xs text-gray-400">{{ row.patient_identifier || '—' }}</div>
                </td>
                <!-- Signal -->
                <td class="px-3 py-2.5 w-[28%] align-top">
                  <span class="flex items-start gap-1.5 text-xs">
                    <span class="w-1.5 h-1.5 rounded-full shrink-0 mt-1" :class="signalDotClass(row.signal_status)"></span>
                    <span class="text-gray-700 leading-snug" :title="row.signal_title">{{ row.signal_title || '—' }}</span>
                  </span>
                </td>
                <!-- Task -->
                <td class="px-3 py-2.5 align-top">
                  <div class="text-xs font-semibold text-gray-900 leading-snug" :title="row.task_title">{{ row.task_title || '—' }}</div>
                  <div v-if="row.task_summary" class="text-xs text-gray-600 leading-snug mt-1">{{ row.task_summary }}</div>
                </td>
                <!-- Assigned (inline reassign) -->
                <td class="px-3 py-2.5 align-top" @click.stop>
                  <CareGraphAssignee
                    :owner-entity-id="row.owner_entity_id"
                    :owner-name="row.owner_name"
                    :members="members"
                    @assign="mid => onReassign(row, mid)"
                    @unassign="onReassign(row, null)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div
          v-if="!loading && total > pageSize"
          class="sticky bottom-0 flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-white text-xs text-gray-500"
        >
          <span>{{ pageStart }}–{{ pageEnd }} of {{ total }}</span>
          <div class="flex items-center gap-2">
            <button class="cm-icon-btn" :disabled="page <= 1" title="Previous page" @click="goToPage(page - 1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 11px;" />
            </button>
            <span>Page {{ page }} of {{ pageCount }}</span>
            <button class="cm-icon-btn" :disabled="!hasMore" title="Next page" @click="goToPage(page + 1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 11px;" />
            </button>
          </div>
        </div>
      </div>

      <!-- Drag handle -->
      <div
        v-if="selectedPatient"
        class="w-1 cursor-col-resize bg-gray-200 hover:bg-primary-400 active:bg-primary-500 transition-colors shrink-0"
        @mousedown="startDrag"
      ></div>

      <!-- Drawer -->
      <div
        v-if="selectedPatient"
        :style="{ width: (100 - splitPct) + '%' }"
        class="flex flex-col overflow-hidden"
      >
        <div class="flex-1 overflow-hidden">
          <Patient_Drawer
            ref="patientDrawer"
            :patient-identifier="selectedPatient.patient_identifier"
            :entity-id="selectedPatient.entity_id"
            :patient-data="selectedPatient"
            :patient-name="selectedPatient.patient_name"
            :enabled-tabs="drawerEnabledTabs"
            :show-header="true"
            :default-tab="'care_plan'"
            metric-category="Care Management"
            @show-message="showMessage"
            @task-updated="loadTasks"
            @close="closeDrawer"
          />
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="message"
        :class="[
          'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 max-w-md',
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        ]"
      >
        <div class="flex items-start">
          <FontAwesomeIcon
            :icon="['fas', message.type === 'success' ? 'check-circle' : 'exclamation-circle']"
            class="mr-2 mt-0.5"
          />
          <div class="font-medium">{{ message.text }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { h } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import CareGraphAssignee from '@/components/Care_Management/CareGraphAssignee.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';

// Tiny inline sort-caret (Vue 3 render function, no template compiler needed).
const SortCaret = {
  name: 'SortCaret',
  props: { active: Boolean, dir: { type: String, default: 'asc' } },
  setup(props) {
    return () => props.active
      ? h('span', { class: 'ml-0.5 text-gray-400' }, props.dir === 'asc' ? '\u25B2' : '\u25BC')
      : null;
  },
};

export default {
  name: 'CareManagementMyTasks',
  components: { FontAwesomeIcon, Patient_Drawer, SortCaret, CareGraphAssignee, ExportMenu },
  data() {
    return {
      loading: false,
      rows: [],
      page: 1,
      pageSize: 100,
      total: 0,
      hasMore: false,
      serverCounts: null,

      searchQuery: '',
      statusFilter: null,
      // Ownership lens. 'my_patients' = to-dos for patients I'm the care
      // manager for (is_my_patient); 'my_todos' = to-dos assigned to me
      // (is_my_todo); null = no personal lens (whole tenant / another CM).
      // Defaults to My Patients per care-advisor request.
      scopeFilter: 'my_patients',
      // Care Manager filter: null = all, 'unassigned' = no owner, or a
      // member id (== owner_entity_id) to view that person's to-dos.
      ownerFilter: null,

      sortKey: 'patient',
      sortDir: 'asc',

      selectedPatient: null,
      selectedTaskId: null,
      splitPct: parseInt(localStorage.getItem('cm_split_pct') || '50'),

      members: [],
      message: null,
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    drawerEnabledTabs() {
      const base = ['details', 'utilization', 'programs', 'engagement', 'care_plan', 'care_graph', 'care_team'];
      if (this.current_tenant_slug === 'ihci') {
        return [...base.filter(t => t !== 'care_plan'), 'insights'];
      }
      return base;
    },
    sortedMembers() {
      return [...this.members].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    filteredTasks() {
      return this.rows;
    },
    queryParams() {
      const params = { sort: this.sortKey, dir: this.sortDir };
      if (this.scopeFilter) params.scope = this.scopeFilter;
      if (this.ownerFilter != null) params.owner = String(this.ownerFilter);
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.searchQuery.trim()) params.q = this.searchQuery.trim();
      return params;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageStart() {
      return this.total ? (this.page - 1) * this.pageSize + 1 : 0;
    },
    pageEnd() {
      return Math.min(this.total, this.page * this.pageSize);
    },
    emptyStateText() {
      if (this.ownerFilter === 'unassigned') return 'No unassigned care-graph to-dos';
      if (this.ownerFilter != null) {
        const m = this.members.find(x => String(x.id) === String(this.ownerFilter));
        return `No open care-graph to-dos for ${m ? m.name : 'that care manager'}`;
      }
      if (this.scopeFilter === 'my_patients') return 'No open care-graph tasks for your patients';
      if (this.scopeFilter === 'my_todos') return 'No care-graph to-dos assigned to you';
      return 'No open care-graph tasks';
    },
    counts() {
      return this.serverCounts || { total: 0, planned: 0, attempted: 0, patients: 0 };
    },
  },
  watch: {
    scopeFilter() { this.reloadFromStart(); },
    ownerFilter() { this.reloadFromStart(); },
    statusFilter() { this.reloadFromStart(); },
    sortKey() { this.reloadFromStart(); },
    sortDir() { this.reloadFromStart(); },
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.reloadFromStart(), 300);
    },
  },
  mounted() {
    this.loadTasks();
    this.loadMembers();
    this._onMessage = (event) => {
      if (event.data?.type === 'clinical-session-saved') this.loadTasks();
    };
    window.addEventListener('message', this._onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this._onMessage);
    clearTimeout(this._searchTimer);
  },
  methods: {
    chipClass(active, color) {
      const on = {
        blue: 'bg-blue-500 text-white border-blue-500',
        gray: 'bg-gray-600 text-white border-gray-600',
      }[color];
      return [
        'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap mt-4',
        active ? on : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
      ];
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },
    setScope(scope) {
      // De-selectable: clicking the active chip clears the lens (show all).
      this.scopeFilter = this.scopeFilter === scope ? null : scope;
    },
    onOwnerFilterChange() {
      // Picking a specific care manager (or Unassigned) is a cross-CM view, so
      // drop the personal lens — otherwise "My Patients" ∩ "Angie" hides rows
      // unexpectedly. "All care managers" leaves the personal lens as-is.
      if (this.ownerFilter != null) this.scopeFilter = null;
    },
    signalDotClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'open') return 'bg-amber-500';
      if (s === 'not_addressed') return 'bg-gray-300';
      return 'bg-gray-300';
    },
    titleCase(str) {
      if (!str) return '';
      return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    reloadFromStart() {
      this.page = 1;
      this.loadTasks();
    },
    goToPage(n) {
      this.page = Math.max(1, n);
      this.loadTasks();
    },
    async loadTasks() {
      const seq = (this._reqSeq = (this._reqSeq || 0) + 1);
      this.loading = true;
      try {
        const { data } = await this.$axios.get('/api/careplannodes/todo/', {
          params: { ...this.queryParams, page: this.page, page_size: this.pageSize },
        });
        if (seq !== this._reqSeq) return;
        if (data?.success) {
          this.rows = data.rows || [];
          this.total = data.total || 0;
          this.hasMore = !!data.has_more;
          this.serverCounts = data.counts || null;
        }
      } catch (e) {
        if (seq !== this._reqSeq) return;
        console.error('Error loading care-graph tasks:', e);
      } finally {
        if (seq === this._reqSeq) this.loading = false;
      }
    },
    async loadMembers() {
      // Tenant's care-team members (tenant-scoped server-side; no hardcoded type).
      try {
        const { data } = await this.$axios.get('/api/team-assignments/summary/');
        this.members = (data || []).map(r => ({ id: r.member_id, name: r.member__name || 'Unknown' }));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },
    async onReassign(row, memberId) {
      // memberId null -> unassign. Optimistic: update the row, then persist.
      const prevId = row.owner_entity_id;
      const prevName = row.owner_name;
      const m = this.members.find(x => String(x.id) === String(memberId));
      row.owner_entity_id = memberId ? Number(memberId) : null;
      row.owner_name = m ? m.name : null;
      try {
        if (memberId) {
          await this.$axios.post(`/api/careplannodes/${row.task_id}/assign/`, { member_id: Number(memberId) });
        } else {
          await this.$axios.post(`/api/careplannodes/${row.task_id}/unassign/`);
        }
      } catch {
        row.owner_entity_id = prevId;  // revert on failure
        row.owner_name = prevName;
        this.showMessage('Reassignment failed', 'error');
      }
    },
    openTask(row) {
      this.selectedTaskId = row.task_id;
      this.selectedPatient = {
        entity_id: row.entity_id,
        patient_identifier: row.patient_identifier,
        patient_name: row.patient_name,
      };
    },
    closeDrawer() {
      this.selectedPatient = null;
      this.selectedTaskId = null;
    },
    startDrag(e) {
      e.preventDefault();
      const container = e.target.parentElement;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        let pct = ((ev.clientX - rect.left) / rect.width) * 100;
        pct = Math.max(25, Math.min(75, pct));
        this.splitPct = Math.round(pct);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('cm_split_pct', String(this.splitPct));
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    async exportCsv() {
      let rows = [];
      try {
        const { data } = await this.$axios.get('/api/careplannodes/todo/', { params: this.queryParams });
        rows = data?.rows || [];
      } catch {
        this.showMessage('Export failed', 'error');
        return;
      }
      if (!rows.length) return;
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const headers = ['Status', 'Patient', 'MBI', 'Task', 'Task Detail', 'Addresses Signal', 'Signal Status', 'Assigned To'];
      const csvRows = rows.map(r => [
        r.task_status || 'planned',
        r.patient_name,
        r.patient_identifier || '',
        r.task_title,
        r.task_summary || '',
        r.signal_title,
        r.signal_status,
        r.owner_name || '',
      ].map(esc).join(','));
      const csv = headers.join(',') + '\n' + csvRows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cm_care_graph_todo_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    showMessage(text, type) {
      this.message = { text, type };
      setTimeout(() => { this.message = null; }, 5000);
    },
  },
};
</script>
